import { Vector3 } from "three";
import convert from "convert-units";

// Helper function to calculate the upper point for snapping
const upperPoints = (start, end, width, point, factor, measured) => {
  width = convert(width * factor[1])
    .from("mm")
    .to(measured);

  let direction = new Vector3().subVectors(end, start).normalize();
  let perpVector = new Vector3(-direction.y, direction.x, 0).multiplyScalar(width);
  const upperLeft = new Vector3().addVectors(point, perpVector);
  return upperLeft;
};

// Utility function to snap a point to existing points based on x and y coordinates
export const getSnappedPoint = (cuuPoint, lastPoint, points, snapActive) => {
  if (!snapActive || points.length === 0) {
    return { snappedPoint: cuuPoint, snapPoints: [] };
  }

  // Get current zoom level from camera context
  const currentZoom = window.cameraContext?.zoom || 1;

  // Calculate dynamic threshold based on zoom level
  // Default threshold is 10 at zoom level 1
  // When zoomed in (zoom > 1), threshold decreases
  // When zoomed out (zoom < 1), threshold increases
  const baseThreshold = 35;
  // const dynamicThreshold = baseThreshold / currentZoom;
  const dynamicThreshold = 10;

  const xMap = new Map();
  const yMap = new Map();
  points.forEach((point) => {
    xMap.set(point.x, point);
    yMap.set(point.y, point);
  });

  const findInMap = (map, callback) => {
    for (let [key, value] of map) {
      if (callback(value, key)) {
        return { key, value };
      }
    }
    return null;
  };

  let snapX, snapY;
  const findX = findInMap(xMap, (value, key) =>
    Math.abs(cuuPoint.x - key) < dynamicThreshold && lastPoint !== value
  );
  const findY = findInMap(yMap, (value, key) =>
    Math.abs(cuuPoint.y - key) < dynamicThreshold && lastPoint !== value
  );

  if (findX) snapX = findX.value;
  if (findY) snapY = findY.value;

  let snappedPoint = new Vector3(cuuPoint.x, cuuPoint.y, 0);
  const snapPoints = [];
  if (snapX && snapX.x !== lastPoint?.x) {
    snappedPoint.x = snapX.x;
    snapPoints.push(snapX);
  }
  if (snapY && snapY.y !== lastPoint?.y) {
    snappedPoint.y = snapY.y;
    snapPoints.push(snapY);
  }

  return { snappedPoint, snapPoints };
};

// Main snapping function with updated threshold
export const snapToPoint = (
  point,
  points,
  storeLines,
  snapActive,
  factor,
  measured,
  snapPoint,
  linePlacementMode
) => {
  if (!snapActive) {
    return point;
  }

  // Get current zoom level from camera context
  const currentZoom = window.cameraContext?.zoom || 1;

  // Calculate dynamic threshold based on zoom level
  // Default threshold is 10 at zoom level 1
  // When zoomed in (zoom > 1), threshold decreases
  // When zoomed out (zoom < 1), threshold increases
  const baseThreshold = 35;
  // const SNAP_THRESHOLD = baseThreshold / currentZoom;
  const SNAP_THRESHOLD = 10;


  for (let p of points) {
    console.log("p", p)
    if (p.distanceTo(point) < SNAP_THRESHOLD) {
      if (snapPoint === "upper" && linePlacementMode === "below") {
        for (let line of storeLines) {
          if (line.points[0].equals(p) || line.points[1].equals(p)) {
            p = upperPoints(
              line.points[0],
              line.points[1],
              line.width,
              p,
              factor,
              measured
            );
          }
        }
      }
      return p;
    }
  }

  for (let line of storeLines) {
    if (line.points[0].distanceTo(point) < SNAP_THRESHOLD) {
      let closestPoint = line.points[0];
      if (snapPoint === "upper" && linePlacementMode === "below") {
        closestPoint = upperPoints(
          line.points[0],
          line.points[1],
          line.width,
          line.points[0],
          factor,
          measured
        );
      }
      return closestPoint;
    }
    if (line.points[1].distanceTo(point) < SNAP_THRESHOLD) {
      let closestPoint = line.points[1];
      if (snapPoint === "upper" && linePlacementMode === "below") {
        closestPoint = upperPoints(
          line.points[0],
          line.points[1],
          line.width,
          line.points[1],
          factor,
          measured
        );
      }
      return closestPoint;
    }

    let closestPointOnLine = closestPointOnSegment(
      line.points[0],
      line.points[1],
      point
    );
    if (closestPointOnLine.distanceTo(point) < SNAP_THRESHOLD) {
      if (snapPoint === "upper" && linePlacementMode === "below") {
        closestPointOnLine = upperPoints(
          line.points[0],
          line.points[1],
          line.width,
          closestPointOnLine,
          factor,
          measured
        );
      }
      return closestPointOnLine;
    }
  }
  return point;
};

// Helper function to find the closest point on a line segment
const closestPointOnSegment = (A, B, P) => {
  const AP = P.clone().sub(A);
  const AB = B.clone().sub(A);
  const magnitudeAB = AB.lengthSq();
  const ABAPproduct = AP.dot(AB);
  const distance = ABAPproduct / magnitudeAB;

  if (distance < 0) {
    return A;
  } else if (distance > 1) {
    return B;
  } else {
    return A.clone().add(AB.multiplyScalar(distance));
  }
};
